import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import ModeSelection from '@/components/ModeSelection';
import EmergencyWorkflowContainer from '@/components/EmergencyWorkflow/EmergencyWorkflowContainer';
import ComprehensiveSessionForm from '@/components/ComprehensiveSessionForm';
import ImageUpload from '@/components/ImageUpload';
import ProcessingView from '@/components/ProcessingView';
import AnalysisView from '@/components/AnalysisView';
import TemporalView from '@/components/TemporalView';
import ReportView from '@/components/ReportView';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { CheckCircle, Camera, BarChart3, Clock, FileText } from 'lucide-react';
import '@/App.css';

const MODES = {
  SELECTION: 'selection',
  EMERGENCY: 'emergency', 
  CONSULTATION: 'consultation'
};

const PHASES = {
  SESSION: 'session',
  UPLOAD: 'upload',
  PROCESSING: 'processing',
  ANALYSIS: 'analysis',
  TEMPORAL: 'temporal',
  REPORT: 'report',
  COMPLETE: 'complete'
};

const PhaseIndicator = ({ currentPhase }) => {
  const phases = [
    { key: PHASES.SESSION, label: 'Session Setup', icon: FileText },
    { key: PHASES.UPLOAD, label: 'Image Capture', icon: Camera },
    { key: PHASES.PROCESSING, label: 'Processing', icon: BarChart3 },
    { key: PHASES.ANALYSIS, label: '3D Analysis', icon: BarChart3 },
    { key: PHASES.TEMPORAL, label: 'Temporal', icon: Clock },
    { key: PHASES.REPORT, label: 'Reports', icon: FileText },
    { key: PHASES.COMPLETE, label: 'Complete', icon: CheckCircle }
  ];

  const currentIndex = phases.findIndex(p => p.key === currentPhase);
  const progress = ((currentIndex + 1) / phases.length) * 100;

  return (
    <div className="w-full bg-white rounded-lg shadow-sm border p-6 mb-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Analysis Progress</h2>
        <span className="text-sm text-gray-500">{Math.round(progress)}% Complete</span>
      </div>
      
      <Progress value={progress} className="mb-4" />
      
      <div className="flex justify-between items-center">
        {phases.map(({ key, label, icon: Icon }, index) => {
          const isActive = index <= currentIndex;
          const isCurrent = key === currentPhase;
          
          return (
            <div key={key} className="flex flex-col items-center space-y-2">
              <div className={`
                w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors
                ${isActive 
                  ? (isCurrent ? 'bg-blue-500 border-blue-500 text-white' : 'bg-green-500 border-green-500 text-white')
                  : 'bg-gray-100 border-gray-300 text-gray-400'
                }
              `}>
                <Icon className="w-5 h-5" />
              </div>
              <span className={`text-xs font-medium ${
                isActive ? 'text-gray-900' : 'text-gray-400'
              }`}>
                {label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Home = () => {
  const [currentMode, setCurrentMode] = useState(MODES.SELECTION);
  const [currentPhase, setCurrentPhase] = useState(PHASES.SESSION);
  const [sessionData, setSessionData] = useState(null);
  const [validationData, setValidationData] = useState(null);
  const [analysisData, setAnalysisData] = useState(null);

  const handleModeSelect = (mode, modeData) => {
    setCurrentMode(mode);
    if (mode === MODES.CONSULTATION) {
      setCurrentPhase(PHASES.SESSION);
    }
    // Emergency mode handled by EmergencyWorkflowContainer
  };

  const handleBackToModeSelection = () => {
    setCurrentMode(MODES.SELECTION);
    setCurrentPhase(PHASES.SESSION);
    setSessionData(null);
    setValidationData(null);
    setAnalysisData(null);
  };

  const handleSessionCreated = (session) => {
    setSessionData(session);
    setCurrentPhase(PHASES.UPLOAD);
  };

  const handleUploadComplete = (validation) => {
    setValidationData(validation);
    if (validation.ready_for_processing) {
      // For quick analysis mode, skip processing and go directly to analysis
      if (validation.quick_analysis_mode) {
        setCurrentPhase(PHASES.ANALYSIS);
      } else {
        setCurrentPhase(PHASES.PROCESSING);
      }
    }
  };

  const handleProcessingComplete = (analysis) => {
    setAnalysisData(analysis);
    setCurrentPhase(PHASES.ANALYSIS);
  };

  const handleAnalysisComplete = (nextPhase) => {
    if (nextPhase === 'temporal') {
      setCurrentPhase(PHASES.TEMPORAL);
    } else {
      setCurrentPhase(PHASES.COMPLETE);
    }
  };

  const handleTemporalComplete = (nextPhase) => {
    if (nextPhase === 'report') {
      setCurrentPhase(PHASES.REPORT);
    } else {
      setCurrentPhase(PHASES.COMPLETE);
    }
  };

  const handleReportComplete = () => {
    setCurrentPhase(PHASES.COMPLETE);
  };

  const handleStartOver = () => {
    setCurrentPhase(PHASES.SESSION);
    setSessionData(null);
    setValidationData(null);
    setAnalysisData(null);
  };

  const renderCurrentPhase = () => {
    switch (currentPhase) {
      case PHASES.SESSION:
        return (
          <ComprehensiveSessionForm 
            onSessionCreated={handleSessionCreated}
          />
        );
      
      case PHASES.UPLOAD:
        return (
          <div className="space-y-6">
            {sessionData && (
              <Card>
                <CardHeader>
                  <CardTitle>Session Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium">Session ID:</span>
                      <p className="text-gray-600">{sessionData.id}</p>
                    </div>
                    <div>
                      <span className="font-medium">Patient ID:</span>
                      <p className="text-gray-600">{sessionData.patient_id}</p>
                    </div>
                    <div>
                      <span className="font-medium">Burn Location:</span>
                      <p className="text-gray-600">{sessionData.burn_location}</p>
                    </div>
                    <div>
                      <span className="font-medium">Burn Cause:</span>
                      <p className="text-gray-600">{sessionData.burn_cause.replace('_', ' ')}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            
            <ImageUpload 
              sessionId={sessionData?.id}
              onUploadComplete={handleUploadComplete}
            />
          </div>
        );
      
      case PHASES.PROCESSING:
        return (
          <ProcessingView 
            sessionId={sessionData?.id}
            onProcessingComplete={handleProcessingComplete}
          />
        );
      
      case PHASES.ANALYSIS:
        return (
          <AnalysisView
            sessionId={sessionData?.id}
            analysisData={analysisData}
            onComplete={handleAnalysisComplete}
          />
        );
      
      case PHASES.TEMPORAL:
        return (
          <TemporalView
            sessionData={sessionData}
            currentAnalysis={analysisData}
            onComplete={handleTemporalComplete}
          />
        );
        
      case PHASES.REPORT:
        return (
          <ReportView
            sessionData={sessionData}
            analysisData={analysisData}
            onComplete={handleReportComplete}
          />
        );
        
      case PHASES.COMPLETE:
        return (
          <Card className="text-center py-12">
            <CardContent>
              <div className="space-y-6">
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Analysis Complete!</h3>
                  <p className="text-gray-600 mb-4">
                    Medical burn 3D reconstruction and analysis successfully completed.
                  </p>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                    <h4 className="font-medium text-green-800 mb-3">Session Summary</h4>
                    <div className="text-left text-sm text-green-700 space-y-1">
                      <p>✅ {validationData?.valid_images || 0} images processed</p>
                      <p>✅ 3D model reconstruction completed</p>
                      <p>✅ Clinical metrics calculated</p>
                      <p>✅ Analysis confidence: {analysisData?.metrics?.confidence || 0}%</p>
                      <p>✅ Session ID: {sessionData?.id}</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      
      default:
        return null;
    }
  };

  // Render mode selection screen
  if (currentMode === MODES.SELECTION) {
    return <ModeSelection onModeSelect={handleModeSelect} />;
  }

  // Render emergency workflow
  if (currentMode === MODES.EMERGENCY) {
    return <EmergencyWorkflowContainer onBack={handleBackToModeSelection} />;
  }

  // Render consultation workflow (existing comprehensive system)
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-8">
        
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-between max-w-4xl mx-auto">
            <button
              onClick={handleBackToModeSelection}
              className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
            >
              ← Back to Mode Selection
            </button>
            <div className="text-center">
              <h1 className="text-4xl font-bold text-gray-900 mb-2">SKINOPATHY</h1>
              <p className="text-gray-600">Medical Consultation System</p>
            </div>
            <div className="w-32"></div> {/* Spacer for centering */}
          </div>
        </div>

        {/* Phase Indicator */}
        <PhaseIndicator currentPhase={currentPhase} />

        {/* Main Content */}
        <div className="max-w-4xl mx-auto">
          {renderCurrentPhase()}
        </div>

        {/* Start Over Button */}
        {currentPhase !== PHASES.SESSION && (
          <div className="text-center mt-8">
            <Button 
              variant="outline" 
              onClick={handleStartOver}
              data-testid="start-over-button"
            >
              Start New Session
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

// Main App Router
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* Main application route - direct access */}
          <Route path="/" element={<Home />} />
          <Route path="/app" element={<Home />} />
          
          {/* Redirect any other route to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
